// src/hooks/useAISuggestions.ts
/**
 * AI 建議管理 Hook
 *
 * 處理 AI 建議的載入、選擇和狀態管理
 * 從 Today.tsx 抽取，保持功能完整性
 */
import { useState, useCallback } from 'react';
import { ImmediateSuggestion, SuggestionRequest } from '../types/suggestion';
import { TimeBoxCategory, CreateTimeBoxRequest } from '../types/timeBox';
import { createTimeBox } from '../services/api/timeBoxes';
import { generateImmediateSuggestions } from '../services/api/suggestions';
import { eventBus, EVENTS } from '../utils/eventBus';
import { logEvent } from '../utils/analytics';
import { logger } from '../utils/logger';

export interface ScheduleItem {
  title: string;
  startTime: Date | string;
}

export interface UseAISuggestionsOptions {
  userId: string;
  onRefresh: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

export interface UseAISuggestionsReturn {
  // 狀態
  showAISuggestion: boolean;
  suggestions: ImmediateSuggestion[];
  overallReason: string | undefined;
  isLoadingSuggestions: boolean;
  suggestionError: string | null;
  // 方法
  loadSuggestions: (nextItem?: ScheduleItem | null) => Promise<void>;
  handleGetAISuggestion: (nextItem?: ScheduleItem | null) => void;
  handleBackToInitial: () => void;
  handleAISuggestionSelect: (suggestion: ImmediateSuggestion) => Promise<void>;
}

export function useAISuggestions(options: UseAISuggestionsOptions): UseAISuggestionsReturn {
  const { userId, onRefresh, onSuccess, onError } = options;

  // AI 建議狀態
  const [showAISuggestion, setShowAISuggestion] = useState(false);
  const [suggestions, setSuggestions] = useState<ImmediateSuggestion[]>([]);
  const [overallReason, setOverallReason] = useState<string | undefined>(undefined);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [suggestionError, setSuggestionError] = useState<string | null>(null);

  /**
   * 載入 AI 建議
   */
  const loadSuggestions = useCallback(async (nextItem?: ScheduleItem | null) => {
    if (!userId) {
      logger.error('AISuggestions', '用戶未登入，終止建議載入');
      setSuggestionError('用戶未登入');
      return;
    }

    const now = new Date();

    setIsLoadingSuggestions(true);
    setSuggestionError(null);
    setSuggestions([]);

    try {
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const request: SuggestionRequest = {
        userContext: {
          currentLocalTime: now.toISOString(),
          timeZone: userTimeZone,
        },
        context: {
          nextEvent: nextItem ? {
            title: nextItem.title,
            startTime: nextItem.startTime instanceof Date
              ? nextItem.startTime.toISOString()
              : nextItem.startTime
          } : undefined
        }
      };

      const response = await generateImmediateSuggestions(request);

      if (response.success) {
        setSuggestions(response.suggestions);
        setOverallReason(response.overallReason || undefined);
      } else {
        logger.error('AISuggestions', 'API 返回失敗狀態');
        setSuggestionError('建議生成失敗，請稍後再試');
      }
    } catch (error) {
      logger.error('AISuggestions', '載入 AI 建議失敗', { error });
      setSuggestionError(
        error instanceof Error
          ? error.message
          : '載入建議時發生未知錯誤'
      );
    } finally {
      setIsLoadingSuggestions(false);
    }
  }, [userId]);

  /**
   * 處理 AI 建議按鈕點擊
   */
  const handleGetAISuggestion = useCallback((nextItem?: ScheduleItem | null) => {
    // 發佈 UI 事件用於 Tip 自動標記
    eventBus.publish(EVENTS.UI.AI_SUGGESTION_TRIGGERED);

    setShowAISuggestion(true);
    loadSuggestions(nextItem);
  }, [loadSuggestions]);

  /**
   * 處理返回初始狀態
   */
  const handleBackToInitial = useCallback(() => {
    setShowAISuggestion(false);
    setSuggestions([]);
    setOverallReason(undefined);
    setSuggestionError(null);
  }, []);

  /**
   * 處理建議選擇 - 創建 TimeBox
   */
  const handleSelectSuggestion = useCallback(async (suggestion: ImmediateSuggestion) => {
    if (!userId) {
      onError('User not logged in');
      return;
    }

    try {
      const now = new Date();
      const startTime = new Date(now);
      const endTime = new Date(now.getTime() + suggestion.estimatedDuration * 60 * 1000);

      // 將建議類型映射到 TimeBox 類別
      let category: TimeBoxCategory;
      switch (suggestion.type) {
        case 'task':
          if (suggestion.metadata?.originalTaskCategory) {
            category = suggestion.metadata.originalTaskCategory === 'work' ? 'focus' : 'life';
          } else {
            category = 'focus';
          }
          break;
        case 'life_activity':
          category = 'life';
          break;
        case 'rest':
          category = 'rest';
          break;
        default:
          category = 'routine';
      }

      const timeBoxRequest: CreateTimeBoxRequest = {
        userId,
        itemType: 'timebox',
        title: suggestion.title,
        notes: suggestion.description,
        startTime,
        endTime,
        category,
        status: 'normal',
        ...(suggestion.metadata?.relatedTaskId && {
          taskIds: [suggestion.metadata.relatedTaskId]
        })
      };

      const createdTimeBox = await createTimeBox(timeBoxRequest);

      // 觸發事件通知其他頁面更新
      eventBus.emit(EVENTS.TIMEBOX.CREATED, createdTimeBox);

      // 重新載入今日數據
      onRefresh();

      onSuccess(`TimeBox created: ${suggestion.title}`);

    } catch (error) {
      logger.error('AISuggestions', '創建時間箱失敗', { error });
      onError(
        error instanceof Error
          ? `Failed to create TimeBox: ${error.message}`
          : 'Unknown error occurred while creating TimeBox'
      );
    }
  }, [userId, onError, onSuccess, onRefresh]);

  /**
   * 處理建議選擇和自動隱藏
   */
  const handleAISuggestionSelect = useCallback(async (suggestion: ImmediateSuggestion) => {
    // 追蹤：用戶點擊 AI 建議
    try {
      logEvent('ai_suggestion_selected', {
        suggestion_type: suggestion.type
      });
    } catch {
      // 靜默失敗
    }

    // 創建 TimeBox
    await handleSelectSuggestion(suggestion);

    // 創建完成後自動隱藏 AISuggestionCard
    handleBackToInitial();
  }, [handleSelectSuggestion, handleBackToInitial]);

  return {
    showAISuggestion,
    suggestions,
    overallReason,
    isLoadingSuggestions,
    suggestionError,
    loadSuggestions,
    handleGetAISuggestion,
    handleBackToInitial,
    handleAISuggestionSelect,
  };
}
